import { useState } from 'react';
import { EPDModel, VerificationType, VerifierType } from 'services/EpdClient';
import { Option } from 'types/types';
import EditDefinitionDialog from '../../dialogs/EditDefinitionDialog';
import EpdDetailsGrid, { EpdDetailsGridRow } from '../EpdDetailsGrid';
import useConformityStandards from '../../wizard/useConformityStandards';
import { ISO_14025_ID } from 'components/v2/compiler/constants';
import { useProductCategories } from 'services/api/standardEpdQueries';

type TProps = {
  epd: EPDModel;
  onChangeEpd: any;
  isReadOnly: boolean;
  title: string | undefined;
  setTitle: any;
};

const EpdDefinition = ({ epd, onChangeEpd, isReadOnly, title, setTitle }: TProps) => {
  const [isDefinitionEditOpened, setIsDefinitionEditOpened] = useState<boolean>(false);
  const conformityStandardsOptions = useConformityStandards(epd?.pcr);

  const { statementOfConformityWithStandards } = epd || {};

  const productCategories = useProductCategories().data;

  function getSelectedStandards(
    statementOfConformityWithStandards: string | undefined,
    conformityStandardsOptions: Option[] | undefined
  ) {
    if (!statementOfConformityWithStandards) return conformityStandardsOptions?.find((y) => y.value === ISO_14025_ID)?.label;

    const standardsArray = statementOfConformityWithStandards?.toString().split(',');

    return standardsArray
      ?.map((x) => conformityStandardsOptions?.find((y) => y.value === x))
      .map((y) => y?.label)
      .join('; ');
  }

  const accreditedCertificationBodyAddress = epd?.epdVerifier?.companyAddress
    ? [
        epd.epdVerifier.companyAddress.country,
        epd.epdVerifier.companyAddress.city,
        epd.epdVerifier.companyAddress.zipCode,
        epd.epdVerifier.companyAddress.addressLine,
      ]
        .filter((value: string | null | undefined) => !!value && value.trim() !== '')
        .join(', ')
    : '';

  return (
    <>
      <EpdDetailsGrid
        headerLabel={'EPD definition'}
        isReadOnly={isReadOnly}
        data={
          [
            { key: 'UUID of data set', value: epd.id },
            { key: 'LCA Practitioner(s)', value: epd.epdDevelopers?.map((x) => x.contactName).join('; ') },
            epd.verificationType === VerificationType.EPDVerification && {
              key: 'EPD Verifier',
              value: epd.epdVerifier?.displayName,
            },
            epd?.epdVerifier?.verifierType === VerifierType.Body
              ? {
                  key: 'Accredited certification body address',
                  value: accreditedCertificationBodyAddress,
                }
              : null,
            { key: 'Product category', value: productCategories?.find((c) => c.id === epd.productCategoryId)?.title },
            { key: 'PCR', value: epd.pcr?.fullName },
            { key: 'C-PCR', value: epd.cpcr?.fullName },
            {
              key: 'Standards conformance',
              value: getSelectedStandards(statementOfConformityWithStandards, conformityStandardsOptions),
            },
            { key: 'Sector EPD', value: epd.isSectorEPD ? 'Yes' : 'No' },
          ].filter(Boolean) as EpdDetailsGridRow[]
        }
        onEdit={() => setIsDefinitionEditOpened(true)}
      />
      <EditDefinitionDialog
        isOpened={isDefinitionEditOpened}
        epd={epd}
        onHide={() => setIsDefinitionEditOpened(false)}
        onChangeEpd={onChangeEpd}
        title={title}
        setTitle={setTitle}
        conformityStandardsOptions={conformityStandardsOptions}
      />
    </>
  );
};

export default EpdDefinition;
